import {YearMonth, formatKpiValue} from '@domain/kpis'
import {KpiType} from '@hconnect/common/types'
import {selectableTableRowSx} from '@hconnect/uikit'
import {Column, DataTable} from '@hconnect/uikit/src/lib2'
import {Typography} from '@mui/material'
import {TFunction} from 'i18next'
import {useMemo} from 'react'
import {useTranslation} from 'react-i18next'

import {FEATURE_NAMESPACE} from '../consts'
import {formatYearMonth} from '../utils'

import {PerformanceReviewKpiTemplates} from './KpiActualPlannedDeltaChip'

export const NO_VALUE_STRING = '-'

type PerformanceReviewTableData = {
  kpiKey: KpiType
  actual?: number
  planned?: number
}

type Props = {
  responseDate?: YearMonth
  data: PerformanceReviewTableData[]
  selectedKpi?: KpiType
  onRowClick?: (kpiType: KpiType) => void
}

export const PerformanceReviewKpisTable = ({
  data,
  onRowClick,
  selectedKpi,
  responseDate
}: Props) => {
  const {t, i18n} = useTranslation(FEATURE_NAMESPACE)
  const language = i18n.language
  const columns = useMemo(
    () => generateColumns({t, language, responseDate}),
    [t, language, responseDate]
  )

  return (
    <DataTable
      data={data}
      columns={columns}
      onRowClick={(_, {kpiKey}) => onRowClick?.(kpiKey)}
      rowSx={({kpiKey}) => selectableTableRowSx(selectedKpi === kpiKey)}
      keyExtractor={(item) => item.kpiKey}
    />
  )
}

const generateColumns = ({
  t,
  language,
  responseDate
}: {
  t: TFunction
  language: string
  responseDate?: YearMonth | undefined
}): Column<PerformanceReviewTableData>[] => [
  {
    key: 'kpiName',
    label: t('kpiTable.label.kpi'),
    customTemplate: ({kpiKey}) => <Typography>{t(`kpis.label.${kpiKey}`)}</Typography>
  },
  {
    key: 'mtd',
    label: t('kpiTable.label.mtd') + (responseDate ? ` (${formatYearMonth(responseDate)})` : ''),
    customTemplate: ({kpiKey, actual, planned}) => {
      if (actual === undefined) {
        return NO_VALUE_STRING
      }
      if (planned === undefined) {
        return formatKpiValue(actual.toString(), language)
      }

      const Template = PerformanceReviewKpiTemplates[kpiKey]
      return (
        <Template actual={formatKpiValue(actual.toString(), language)} diff={actual - planned} />
      )
    }
  }
]
